import React from "react";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/hooks/useSeo";
import { Plane, Building2, FileBadge, Landmark, HeartPulse, Crown, Eye, Target, Gem } from "lucide-react";
import { motion } from "framer-motion";

const ICONS = [Plane, Building2, FileBadge, Landmark, HeartPulse, Crown];

export default function About() {
  const { t } = useApp();
  useSeo({ title: t.seo.about[0], description: t.seo.about[1] });
  const a = t.about;
  return (
    <div className="bg-sand min-h-screen" data-testid="about-page">
      <section className="bg-tigris text-white px-4 sm:px-6 pt-14 pb-20">
        <div className="max-w-5xl mx-auto">
          <p className="text-gold font-semibold text-sm mb-3">{t.brand}</p>
          <h1 className="text-4xl sm:text-5xl font-bold font-display mb-6" data-testid="about-title">{a.title}</h1>
          <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-3xl">{a.intro}</p>
          <p className="text-white/75 leading-relaxed max-w-3xl mt-4">{a.philosophy}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-10">
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-10">
          <h2 className="text-base md:text-lg font-bold text-slate-800 mb-6">{a.servicesTitle}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {a.services.map((s, i) => {
              const Icon = ICONS[i];
              return (
                <motion.div key={s} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  data-testid={`about-service-${i}`} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-4">
                  <span className="w-10 h-10 rounded-xl bg-tigris/10 text-tigris flex items-center justify-center shrink-0"><Icon size={20} /></span>
                  <span className="text-sm font-semibold text-slate-700">{s}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 grid md:grid-cols-3 gap-6">
        {[{ Icon: Eye, title: a.visionTitle, body: a.vision }, { Icon: Target, title: a.missionTitle, body: a.mission }, { Icon: Gem, title: a.valuesTitle, body: a.values }].map(({ Icon, title, body }) => (
          <div key={title} className="bg-white rounded-3xl p-7 border border-slate-100 shadow-sm">
            <span className="w-12 h-12 rounded-2xl bg-gold/15 text-gold flex items-center justify-center mb-4"><Icon size={24} /></span>
            <h3 className="font-bold text-slate-800 text-lg mb-2">{title}</h3>
            <p className="text-slate-600 text-sm leading-relaxed">{body}</p>
          </div>
        ))}
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 text-center">
        <p className="text-tigris font-display text-2xl sm:text-3xl font-bold" data-testid="about-slogan">{a.slogan}</p>
      </section>
    </div>
  );
}
